import { useState, useEffect } from 'react';
import { sugerenciasCompraAPI } from '../services/sugerenciasCompraAPI';
import { ordenesCompraAPI } from '../services/ordenesCompraAPI';
import { useToast } from './useToast';
import type { OrdenCompra } from '../types/ordenes';

// Interfaces específicas para este hook
export interface SugerenciaCompra {
  ingrediente_id: number;
  nombre_ingrediente: string;
  categoria: string;
  cantidad_necesaria: number;
  stock_actual: number;
  cantidad_sugerida: number;
  unidad: string;
  proveedor_principal: string;
  costo_unitario: number; // Precio por unidad (ej: $12,500/kg)
  costo_estimado: number; // Precio total (cantidad × precio unitario)
  prioridad: 'alta' | 'media' | 'baja';
  dias_stock_restante: number;
  // Fase 6: unidad base + formato de compra preferido
  cantidad_necesaria_base?: number | null;
  cantidad_sugerida_base?: number | null;
  stock_actual_base?: number | null;
  unidad_base_simbolo?: string | null;
  unidad_display_simbolo?: string | null;
  tipo_fisico?: 'peso' | 'volumen' | 'cantidad' | null;
  formato_compra_sugerido?: string | null;
  cantidad_packs_sugerida?: number | null;
  unidades_por_pack?: number | null;
  unidad_pack_simbolo?: string | null;
  // 'demanda' = pedidos del período lo necesitan
  // 'reposicion' = sin demanda, stock_actual <= stock_minimo
  motivo?: 'demanda' | 'reposicion';
  stock_minimo?: number;
}

export interface SugerenciasResponse {
  fecha_calculo: string;
  parametros: {
    dias_adelante: number;
    porcentaje_extra: number;
    incluir_stock: boolean;
  };
  sugerencias_por_proveedor: {
    [proveedor: string]: {
      ingredientes: SugerenciaCompra[];
      total_estimado: number;
    };
  };
  resumen: {
    total_ingredientes: number;
    total_proveedores: number;
    costo_total_estimado: number;
    ingredientes_criticos: number;
  };
}

export interface ParametrosSugerencias {
  diasAdelante: number;
  porcentajeExtra: number;
  incluirStock: boolean;
}

export interface UsComprasReturn {
  // Estados de datos
  sugerencias: SugerenciasResponse | null;
  ordenes: OrdenCompra[];
  historial: OrdenCompra[];

  // Estados de carga
  cargandoSugerencias: boolean;
  cargandoOrdenes: boolean;
  cargandoHistorial: boolean;

  // Estados de error
  errorSugerencias: string | null;
  errorOrdenes: string | null;
  errorHistorial: string | null;

  // Funciones
  generarSugerencias: (
    parametros: ParametrosSugerencias,
    mostrarToast?: boolean
  ) => Promise<void>;
  crearOrdenesDesdesugerencias: () => Promise<void>;
  actualizarEstadoOrden: (
    ordenId: string,
    nuevoEstado: OrdenCompra['estado']
  ) => Promise<void>;
  marcarComoRecibida: (
    ordenId: string,
    itemsRecibidos: {
      ingrediente_id: number;
      cantidad_recibida: number;
      precio_unitario?: number;
    }[]
  ) => Promise<void>;
  recargarDatos: () => Promise<void>;
}

export const useCompras = (): UsComprasReturn => {
  // Toast notifications
  const { success: toastSuccess, error: toastError } = useToast();

  // Estados de datos
  const [sugerencias, setSugerencias] = useState<SugerenciasResponse | null>(
    null
  );
  const [ordenes, setOrdenes] = useState<OrdenCompra[]>([]);
  const [historial, setHistorial] = useState<OrdenCompra[]>([]);

  // Estados de carga
  const [cargandoSugerencias, setCargandoSugerencias] = useState(false);
  const [cargandoOrdenes, setCargandoOrdenes] = useState(false);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);

  // Estados de error
  const [errorSugerencias, setErrorSugerencias] = useState<string | null>(null);
  const [errorOrdenes, setErrorOrdenes] = useState<string | null>(null);
  const [errorHistorial, setErrorHistorial] = useState<string | null>(null);

  // Función para generar sugerencias
  const generarSugerencias = async (
    parametros: ParametrosSugerencias,
    mostrarToast: boolean = true
  ) => {
    try {
      // Limpiar sugerencias anteriores inmediatamente
      setSugerencias(null);
      setCargandoSugerencias(true);
      setErrorSugerencias(null);

      console.log('🔄 Generando nuevas sugerencias desde pedidos futuros...');

      // Usar el nuevo endpoint basado en pedidos futuros
      const response = await sugerenciasCompraAPI.generarDesdePedidos(
        parametros.diasAdelante
      );

      // El backend ya devuelve los datos en el formato correcto
      const datosBackend: any = response.data;

      // Adaptar al formato esperado por el componente frontend
      const datosAdaptados: SugerenciasResponse = {
        fecha_calculo: datosBackend.fecha_calculo || new Date().toISOString(),
        parametros: {
          dias_adelante: parametros.diasAdelante,
          porcentaje_extra: parametros.porcentajeExtra,
          incluir_stock: parametros.incluirStock,
        },
        sugerencias_por_proveedor: datosBackend.sugerencias_por_proveedor || {},
        resumen: {
          total_ingredientes: datosBackend.resumen?.total_ingredientes || 0,
          total_proveedores:
            datosBackend.resumen?.total_proveedores ||
            Object.keys(datosBackend.sugerencias_por_proveedor || {}).length,
          costo_total_estimado: datosBackend.resumen?.costo_total_estimado || 0,
          ingredientes_criticos:
            datosBackend.resumen?.ingredientes_urgentes || 0,
        },
      };

      setSugerencias(datosAdaptados);

      // Solo mostrar toast si se solicita explícitamente (acciones manuales)
      if (mostrarToast) {
        toastSuccess(
          `Sugerencias generadas: ${datosAdaptados.resumen.total_ingredientes} ingredientes encontrados`
        );
      }
    } catch (error) {
      console.error('Error al generar sugerencias:', error);
      setErrorSugerencias('Error al generar sugerencias de compra');
      toastError('Error al generar sugerencias de compra');
    } finally {
      setCargandoSugerencias(false);
    }
  };

  // Función para crear órdenes desde sugerencias
  const crearOrdenesDesdesugerencias = async () => {
    try {
      if (!sugerencias) {
        toastError('No hay sugerencias disponibles para crear órdenes');
        return;
      }

      setCargandoOrdenes(true);

      const resultado = await ordenesCompraAPI.crearDesdesugerencias({
        sugerencias: [],
      });

      setOrdenes(resultado.data.ordenes);
      toastSuccess(
        `${resultado.data.resumen.ordenes_creadas} órdenes de compra creadas exitosamente`
      );

      // Recargar datos
      await cargarOrdenes();
    } catch (error) {
      console.error('Error al crear órdenes:', error);
      toastError('Error al crear órdenes de compra');
    } finally {
      setCargandoOrdenes(false);
    }
  };

  // Función para actualizar estado de orden
  const actualizarEstadoOrden = async (
    ordenId: string,
    nuevoEstado: OrdenCompra['estado']
  ) => {
    try {
      // Mapear estados a métodos de API
      switch (nuevoEstado) {
        case 'enviada':
          await ordenesCompraAPI.enviar(ordenId);
          break;
        case 'cancelada':
          await ordenesCompraAPI.cancelar(ordenId);
          break;
        default:
          throw new Error(
            `Estado ${nuevoEstado} no puede ser actualizado directamente`
          );
      }

      // Actualizar estado local
      setOrdenes((prev) =>
        prev.map((orden) =>
          orden.id === ordenId ? { ...orden, estado: nuevoEstado } : orden
        )
      );

      toastSuccess(`Estado de orden actualizado a: ${nuevoEstado}`);
    } catch (error) {
      console.error('Error al actualizar estado de orden:', error);
      toastError('Error al actualizar estado de orden');
    }
  };

  // Función para marcar orden como recibida
  const marcarComoRecibida = async (
    ordenId: string,
    itemsRecibidos: {
      ingrediente_id: number;
      cantidad_recibida: number;
      precio_unitario?: number;
    }[]
  ) => {
    try {
      // Convertir formato para la API
      const datosRecepcion = {
        fecha_recepcion: new Date().toISOString(),
        observaciones_recepcion: 'Recepción procesada desde la aplicación',
        items: itemsRecibidos.map((item) => ({
          detalle_id: item.ingrediente_id, // Asumiendo que detalle_id corresponde al ingrediente
          cantidad_recibida: item.cantidad_recibida,
          observaciones: item.precio_unitario
            ? `Precio: $${item.precio_unitario}`
            : undefined,
        })),
      };

      await ordenesCompraAPI.recepcionar(ordenId, datosRecepcion);

      // Actualizar estado local
      setOrdenes((prev) =>
        prev.map((orden) =>
          orden.id === ordenId
            ? {
                ...orden,
                estado: 'recibida' as const,
                fechaRecepcion: new Date().toISOString(),
              }
            : orden
        )
      );

      toastSuccess('Orden marcada como recibida e inventario actualizado');

      // Recargar datos
      await cargarOrdenes();
      await cargarHistorial();
    } catch (error) {
      console.error('Error al marcar orden como recibida:', error);
      toastError('Error al procesar recepción de orden');
    }
  };

  // Función para cargar órdenes activas
  const cargarOrdenes = async () => {
    try {
      setCargandoOrdenes(true);
      setErrorOrdenes(null);

      const datos = await ordenesCompraAPI.listarActivas();
      setOrdenes(datos);
    } catch (error) {
      console.error('Error al cargar órdenes:', error);
      setErrorOrdenes('Error al cargar órdenes de compra');
    } finally {
      setCargandoOrdenes(false);
    }
  };

  // Función para cargar historial
  const cargarHistorial = async () => {
    try {
      setCargandoHistorial(true);
      setErrorHistorial(null);

      const datos = await ordenesCompraAPI.listarCompletadas();
      setHistorial(datos);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setErrorHistorial('Error al cargar historial de órdenes');
    } finally {
      setCargandoHistorial(false);
    }
  };

  // Función para recargar todos los datos
  const recargarDatos = async () => {
    await Promise.all([cargarOrdenes(), cargarHistorial()]);
  };

  // Cargar datos iniciales
  useEffect(() => {
    recargarDatos();
  }, []);

  return {
    // Estados de datos
    sugerencias,
    ordenes,
    historial,

    // Estados de carga
    cargandoSugerencias,
    cargandoOrdenes,
    cargandoHistorial,

    // Estados de error
    errorSugerencias,
    errorOrdenes,
    errorHistorial,

    // Funciones
    generarSugerencias,
    crearOrdenesDesdesugerencias,
    actualizarEstadoOrden,
    marcarComoRecibida,
    recargarDatos,
  };
};
